import React, { useEffect, useMemo, useState } from "react"

import algoliasearch from "algoliasearch/lite"
import { SearchState } from "react-instantsearch-core"
import { InstantSearch } from "react-instantsearch-dom"
import { IntlShape, useIntl } from "react-intl"

import FilteredPagesJsonLd from "./FilteredPagesJsonLd"
import { FormattedBanner } from "~/components/Banner/utils"
import FilteredRecipesHeader from "~/components/FilteredRecipes/FilteredRecipesHeader/FilteredRecipesHeader"
import FilteredRecipesList from "~/components/FilteredRecipes/FilteredRecipesList/FilteredRecipesList"
import SEO from "~/components/Seo/Seo"
import { Container } from "~/components/ui/Container/Container"
import useCategories, { AllCategories } from "~/hooks/useCategories"
import { RecipeAlgoliaHit } from "~/models/Recipe"
import { ResultState } from "~/templates/Category/types"

import css from "./FilteredRecipes.module.scss"

const TIME_BEFORE_REFRESH_URL = 400

const GATSBY_ALGOLIA_API_KEY = process.env.GATSBY_ALGOLIA_API_KEY ?? ""
const GATSBY_ALGOLIA_APPLICATION_ID =
  process.env.GATSBY_ALGOLIA_APPLICATION_ID ?? ""
const GATSBY_ALGOLIA_INDEX = process.env.GATSBY_ALGOLIA_INDEX ?? ""

type SSGPropType = {
  baseSearchState: SearchState
  baseResultState: ResultState<RecipeAlgoliaHit>
  baseHierarchicalFacetsItems: any[]
  baseVerticalRefinementListItems: any[]
  baseStarIngredientsRefinementListItems: any[]
}

type urlPropType = {
  baseSlug: string
  createURL: (
    state: SearchState,
    CATEGORIES: AllCategories,
    baseSlug: string
  ) => any
  urlToSearchState: (
    search: string,
    pathname: string,
    hash: string,
    CATEGORIES: AllCategories
  ) => SearchState
  searchStateToUrl: (
    searchState: SearchState,
    CATEGORIES: AllCategories,
    intl: IntlShape
  ) => any
}

type locationType = {
  pathname: string
  location: Location
}

type PropTypes = SSGPropType &
  urlPropType &
  locationType & {
    refreshTitle: (
      searchState: SearchState,
      CATEGORIES: AllCategories,
      isSSG: boolean,
      nbHits: number
    ) => {
      pageH1?: string
      pageTitle: string
      description?: string
      descriptionSEO?: string
    }
    bannerToDisplay: FormattedBanner | null
  }

function FilteredRecipes({
  baseSearchState,
  baseResultState,
  baseHierarchicalFacetsItems,
  baseVerticalRefinementListItems,
  baseStarIngredientsRefinementListItems,

  createURL,
  urlToSearchState,
  searchStateToUrl,
  baseSlug,

  location,
  pathname,

  refreshTitle,
  bannerToDisplay,
}: PropTypes) {
  const intl = useIntl()
  const CATEGORIES = useCategories(intl.locale)

  const searchClient = useMemo(
    () => algoliasearch(GATSBY_ALGOLIA_APPLICATION_ID, GATSBY_ALGOLIA_API_KEY),
    []
  )

  const [searchState, setSearchState] = useState<SearchState>(baseSearchState)
  const [debouncedSetState, setDebouncedSetState] = useState<any>()
  const [nbHits, setNbHits] = useState(-1)

  useEffect(() => {
    const search = urlToSearchState(
      location.search,
      pathname,
      location.hash,
      CATEGORIES
    )

    setSearchState(search)
  }, [])

  const { pageH1, pageTitle, description, descriptionSEO } = refreshTitle(
    searchState,
    CATEGORIES,
    searchState == baseSearchState,
    nbHits
  )

  const firstHit = baseResultState?.hits && baseResultState?.hits[0]

  const image = firstHit && baseResultState?.hits[0].web_cover_thumbnail

  const onSearchStateChange = async (updatedSearchState: any) => {
    // Refresh URL
    clearTimeout(debouncedSetState)
    setDebouncedSetState(
      window.setTimeout(() => {
        return window.history.pushState(
          updatedSearchState,
          "",
          "/" +
            intl.locale +
            searchStateToUrl(updatedSearchState, CATEGORIES, intl)
        )
      }, TIME_BEFORE_REFRESH_URL)
    )
    setSearchState(updatedSearchState)
  }

  return (
    <Container className={css.HitsByCategories}>
      <SEO
        title={pageTitle}
        description={descriptionSEO ?? undefined}
        image={image}
        needsCanonical={!searchState.query}
      />
      <FilteredPagesJsonLd
        baseResultState={baseResultState}
        listingTitle={pageTitle}
        listingSEODescription={descriptionSEO ?? description}
      />
      <InstantSearch
        searchClient={searchClient}
        indexName={GATSBY_ALGOLIA_INDEX}
        searchState={searchState ?? {}}
        onSearchStateChange={onSearchStateChange}
        createURL={(state: any) => {
          return createURL(state, CATEGORIES, baseSlug)
        }}
      >
        <FilteredRecipesHeader
          location={location}
          title={pageH1 ?? pageTitle}
          description={
            description ??
            intl.formatMessage({
              id: "category/text:default-description",
            })
          }
          baseHierarchicalFacetsItems={baseHierarchicalFacetsItems}
          ingredient={
            searchState.refinementList &&
            searchState.refinementList["star_ingredients.slug"] &&
            searchState.refinementList["star_ingredients.slug"].length
              ? searchState.refinementList["star_ingredients.slug"][0]
              : undefined
          }
          bannerToDisplay={bannerToDisplay}
        />
        <FilteredRecipesList
          baseResultState={baseResultState}
          baseHierarchicalFacetsItems={baseHierarchicalFacetsItems}
          baseVerticalRefinementListItems={baseVerticalRefinementListItems}
          baseStarIngredientsRefinementListItems={
            baseStarIngredientsRefinementListItems
          }
          searchState={searchState}
          setSearchState={setSearchState}
          setNbHits={setNbHits}
        />
      </InstantSearch>
    </Container>
  )
}

export default FilteredRecipes
